import { useState, useRef, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { UploadedDocument, Mode } from "./types";

export interface SseAnalysisOptions {
  mode: Mode;
  documents?: UploadedDocument[];
  extraText?: string;
  isTenderDoc?: boolean | null;
  tenderId?: number;
  companyProfileId?: number;
}

export function useSseAnalysis() {
  const { getToken } = useAuth();
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const runAnalysis = useCallback(async (opts: SseAnalysisOptions) => {
    setResult("");
    setError("");
    setLoading(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getToken();
      const response = await fetch("/api/admin/ai-assistant/analyze", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal: controller.signal,
        body: JSON.stringify({ ...opts, llm: "gemini" }),
      });

      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error ?? `伺服器錯誤 (${response.status})`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.error) { setError(data.error); break; }
            if (data.text) {
              setResult(prev => prev + data.text);
            }
          } catch {}
        }
      }
      
      if (buf.trim().startsWith("data: ")) {
        const payload = buf.slice(6).trim();
        if (payload !== "[DONE]") {
          try {
            const data = JSON.parse(payload);
            if (data.error) setError(data.error);
            if (data.text) setResult(prev => prev + data.text);
          } catch {}
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message ?? "分析失敗，請稍後重試。");
      }
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setLoading(false);
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setResult("");
    setError("");
    setLoading(false);
  }, []);

  return { result, error, loading, runAnalysis, abort, reset };
}
